// A download link that carries the auth token.
//
// WHY THIS EXISTS
// ---------------
// lib/api-fetch.ts attaches the bearer token to everything the app fetches —
// but a plain <a href="/api/invoices/123/pdf"> is a browser NAVIGATION, not a
// fetch. It never goes through window.fetch, so it carries no Authorization
// header at all.
//
// That was invisible while the API checked nothing. The moment the global auth
// gate went live (api/src/lib/require-auth-hook.ts), all eight download links
// in the app — every invoice XLSX and PDF, the affiliate payment backup, the
// adjustments export — would have started returning 401. Invoice downloads are
// the daily job here, so that is an outage, not a papercut.
//
// So the download is done as a fetch (token attached automatically), turned
// into a blob, and handed to the browser through a temporary object URL.
//
// WHAT THIS IS NOT
// ----------------
// This is not a way to make a protected file public. The request is still
// authenticated and still authorised server-side; the only thing that changes
// is that the token travels with it.
//
// Trade-off worth knowing: the file is buffered in memory before it is saved,
// so the browser shows no native progress bar and a very large export will sit
// on "Preparing…" for a while. Invoice XLSX/PDFs here are tens of KB, so it
// does not matter. If a genuinely large export appears later, the better shape
// is a short-lived signed download token in the URL.

import { useState } from 'react'

interface Props {
  /** Same-origin API path, e.g. `/api/invoices/INV-1/pdf` */
  href: string
  /**
   * Filename for the saved file. Falls back to the server's
   * Content-Disposition.
   *
   * Accepts a bare `download` too (JSX passes boolean true), matching how the
   * <a download> attribute this replaced was written at most call sites —
   * there it means "save it, name it whatever the server says".
   */
  download?: string | boolean
  className?: string
  children: React.ReactNode
  /** Called with a human-readable message if the download fails. */
  onError?: (message: string) => void
}

export function DownloadLink({ href, download, className, children, onError }: Props) {
  const [busy, setBusy] = useState(false)

  async function run(e: React.MouseEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)

    let objectUrl: string | null = null
    try {
      // No headers passed: lib/api-fetch.ts adds the token for same-origin
      // /api/ paths. Keeping it implicit means this component cannot drift out
      // of sync with how the rest of the app authenticates.
      const res = await fetch(href)

      if (!res.ok) {
        // A 401 is already handled globally (api-fetch.ts bounces to login),
        // so anything landing here is a real server-side failure worth showing
        // rather than swallowing — a silently dead download button is how you
        // end up thinking an invoice was never generated.
        let detail = `${res.status} ${res.statusText}`
        try {
          const body = await res.json()
          if (body?.error) detail = body.error
        } catch { /* not JSON — keep the status line */ }
        throw new Error(detail)
      }

      const blob = await res.blob()
      objectUrl = URL.createObjectURL(blob)

      // Prefer the server's filename when it sent one; it knows the invoice
      // number and the real extension.
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
      const filename =
        typeof download === 'string' && download
          ? download
          : match ? decodeURIComponent(match[1]) : 'download'

      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Download failed')
    } finally {
      // Revoking immediately can cancel the save in some browsers, so give the
      // click a moment to be picked up first.
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl!), 10_000)
      setBusy(false)
    }
  }

  return (
    // Still an <a> so it looks and reads like a link, but href="#" plus
    // preventDefault means the browser never navigates to it unauthenticated.
    <a href="#" onClick={run} className={className} aria-busy={busy || undefined}>
      {busy ? 'Preparing…' : children}
    </a>
  )
}
